import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { acknowledgeTemplateMessage } from '../../services/messageTemplateService';
import websocketService from '../../services/websocketService';
import { fetchTaskById } from '../../store/slices/taskSlice';
import TaskViewDialog from '../tasks/TaskViewDialog';
import WorkflowViewDialog from '../chat/WorkflowViewDialog';
import { messageToWorkflowItem } from '../../utils/workflowTaskUtils';
import { fetchEvent, respondToEvent } from '../../features/events/services/eventService';
import { eventDtoToTimelineInfo } from '../../features/events/utils/eventTimelineUtils';
import PollVoteBlock from '../../features/events/components/PollVoteBlock';

const BLAST_COLORS = [
  '#F43F5E', '#FB923C', '#FBBF24', '#34D399', '#22D3EE',
  '#60A5FA', '#A78BFA', '#F472B6', '#FDE68A', '#4ADE80',
];

const buildBlastPieces = (count = 56) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
    const distance = 120 + Math.random() * 220;
    return {
      id: i,
      color: BLAST_COLORS[i % BLAST_COLORS.length],
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance - 40,
      rot: Math.floor(Math.random() * 720 - 360),
      size: 6 + Math.random() * 10,
      delay: Math.random() * 0.25,
      duration: 0.9 + Math.random() * 0.7,
      shape: i % 3,
    };
  });

const stripHtml = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Drop duplicated "Title (Announcement)" / "Posted by …" lines from body text. */
const cleanAnnouncementBody = (content, title) => {
  if (!content) return '';
  let text = String(content).trim();
  if (title) {
    const escaped = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text.replace(new RegExp(`^${escaped}\\s*\\(?\\s*Announcement\\s*\\)?\\s*`, 'i'), '');
    text = text.replace(new RegExp(`^${escaped}\\s*`, 'i'), '');
  }
  text = text.replace(/^\(?\s*Announcement\s*\)?\s*/i, '');
  text = text.replace(/\n?\s*Posted by\s+.+$/i, '');
  return text.trim();
};

const mapPanelAnnouncement = (ann) => ({
  source: 'panel',
  id: ann.id,
  title: ann.title,
  content: ann.content,
  createdByName: ann.createdByName,
  createdAt: ann.publishedAt || ann.createdAt,
  expiresAt: ann.expiresAt,
  category: ann.category,
  type: ann.type || 'INFO',
  eventId: ann.eventId || null,
});

const mapTemplateAnnouncement = (ann) => {
  const title = ann.title || ann.templateName || 'Announcement';
  return {
    source: 'template',
    id: ann.id,
    title,
    content: cleanAnnouncementBody(ann.content || '', title),
    createdByName: ann.createdByName || 'Someone',
    createdAt: ann.createdAt,
    expiresAt: null,
    category: null,
    type: ann.type || 'INFO',
    channelId: ann.channelId,
    linkedItems: Array.isArray(ann.linkedItems) ? ann.linkedItems : [],
  };
};

const mapFromChatMessage = (message) => {
  const info = message?.templateInfo;
  if (!info) return null;
  const type = (info.templateType || '').toUpperCase();
  if (type !== 'ANNOUNCEMENT' || !info.requiresAcknowledge) return null;
  const title = info.templateName || 'Announcement';
  return {
    source: 'template',
    id: message.id,
    title,
    content: cleanAnnouncementBody(stripHtml(message.content), title),
    createdByName: message.senderName
      || `${message.senderFirstName || ''} ${message.senderLastName || ''}`.trim()
      || 'Someone',
    createdAt: message.createdAt,
    expiresAt: null,
    category: null,
    type: 'INFO',
    channelId: message.channelId,
    linkedItems: Array.isArray(info.linkedItems) ? info.linkedItems : [],
  };
};

const AnnouncementPollPreview = ({ eventId, currentUserId }) => {
  const [eventInfo, setEventInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventId) {
      setEventInfo(null);
      return undefined;
    }
    let cancelled = false;
    fetchEvent(eventId)
      .then((dto) => {
        if (cancelled) return;
        setEventInfo(eventDtoToTimelineInfo(dto));
      })
      .catch(() => {
        if (!cancelled) setEventInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (!eventInfo) return null;
  const closed = ['CLOSED', 'CANCELLED', 'EXPIRED'].includes(String(eventInfo.status || '').toUpperCase());
  const canVote = Boolean(currentUserId)
    && !closed
    && String(eventInfo.creatorId) !== String(currentUserId);

  const handleVote = async (selectedOptions) => {
    if (!canVote || saving) return;
    setSaving(true);
    try {
      const dto = await respondToEvent(eventId, {
        response: selectedOptions.length ? 'VOTE' : 'UNVOTE',
        rsvpDetails: { selectedOptions },
      });
      setEventInfo(eventDtoToTimelineInfo(dto));
    } catch {
      // keep current preview
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-gray-700">
      <PollVoteBlock
        eventInfo={eventInfo}
        currentUserId={currentUserId}
        canVote={canVote}
        saving={saving}
        onVote={handleVote}
        compact
      />
    </div>
  );
};

const AnnouncementPopup = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);
  const location = useLocation();
  const hideOnAnnouncementPanel = location.pathname.startsWith('/announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acknowledging, setAcknowledging] = useState(false);
  const [blastKey, setBlastKey] = useState(0);
  const [viewingTask, setViewingTask] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [openingLinkId, setOpeningLinkId] = useState('');

  const blastPieces = useMemo(() => buildBlastPieces(56), [blastKey]);

  const mergeAnnouncements = useCallback((incoming) => {
    setAnnouncements((prev) => {
      const byKey = new Map();
      [...prev, ...incoming].forEach((item) => {
        if (!item?.id) return;
        byKey.set(`${item.source}:${item.id}`, item);
      });
      return Array.from(byKey.values()).sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
    });
  }, []);

  const fetchActiveAnnouncements = useCallback(async () => {
    if (!token) return;
    try {
      const [panelRes, templateRes] = await Promise.all([
        axios.get(`${API_URL}/announcements/active`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/message-templates/announcements/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const now = Date.now();
      const stillLive = (item) => {
        if (!item?.expiresAt) return true;
        const ends = new Date(item.expiresAt).getTime();
        return Number.isFinite(ends) && ends > now;
      };
      const panelList = (Array.isArray(panelRes.data) ? panelRes.data : [])
        .map(mapPanelAnnouncement)
        .filter(stillLive);
      const templateList = (Array.isArray(templateRes.data) ? templateRes.data : []).map(mapTemplateAnnouncement);
      const combined = [...panelList, ...templateList];

      setAnnouncements(combined);
      setCurrentIndex((prev) => {
        if (combined.length === 0) return 0;
        return Math.min(prev, combined.length - 1);
      });
    } catch (error) {
      console.error('Failed to fetch active announcements:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!token || hideOnAnnouncementPanel) return undefined;
    fetchActiveAnnouncements();
    const interval = setInterval(fetchActiveAnnouncements, 20000);
    return () => clearInterval(interval);
  }, [token, fetchActiveAnnouncements, hideOnAnnouncementPanel]);

  useEffect(() => {
    if (announcements.length > 0) {
      setBlastKey((k) => k + 1);
    }
  }, [announcements.length, currentIndex]);

  // Live template announcements via websocket topic
  useEffect(() => {
    if (!user?.id) return undefined;

    const topic = `/topic/template-announcements/${user.id}`;
    const eventTopic = `/topic/announcements/${user.id}`;
    const handler = (data) => {
      const ann = data?.announcement || data;
      if (!ann?.id) return;
      mergeAnnouncements([mapTemplateAnnouncement(ann)]);
      setCurrentIndex(0);
    };
    const eventHandler = (data) => {
      const ann = data?.announcement || data;
      if (!ann?.id) return;
      mergeAnnouncements([mapPanelAnnouncement(ann)]);
      setCurrentIndex(0);
    };

    websocketService.subscribeToTopic(topic, handler);
    websocketService.subscribeToTopic(eventTopic, eventHandler);

    const onCustom = (event) => {
      const mapped = mapFromChatMessage(event?.detail);
      if (!mapped) return;
      if (mapped.id && user.id && event?.detail?.senderId === user.id) return;
      const acks = event?.detail?.templateInfo?.acknowledgedByUserIds || [];
      if (acks.includes(user.id)) return;
      mergeAnnouncements([mapped]);
      setCurrentIndex(0);
    };
    window.addEventListener('workloop:template-announcement', onCustom);

    return () => {
      websocketService.unsubscribeFromTopic(topic);
      websocketService.unsubscribeFromTopic(eventTopic);
      window.removeEventListener('workloop:template-announcement', onCustom);
    };
  }, [user?.id, mergeAnnouncements]);

  const dismissAnnouncement = (item) => {
    if (!item) return;
    setAnnouncements((prev) => {
      const remaining = prev.filter(
        (ann) => !(ann.id === item.id && ann.source === item.source)
      );
      setCurrentIndex((idx) => {
        if (remaining.length === 0) return 0;
        return Math.min(idx, remaining.length - 1);
      });
      return remaining;
    });
    if (item.eventId) {
      window.dispatchEvent(new CustomEvent('workloop:open-event', {
        detail: { eventId: item.eventId },
      }));
    }
  };

  const isDismissibleAckError = (error) => {
    const status = error?.response?.status;
    const raw = error?.response?.data;
    const message = typeof raw === 'string'
      ? raw
      : (raw?.error || raw?.message || error?.message || '');
    const text = String(message);
    return status === 400 || status === 404
      || /not active|not found|no longer available/i.test(text);
  };

  const handleAcknowledge = async (item) => {
    if (acknowledging || !item) return;
    try {
      setAcknowledging(true);
      if (item.source === 'template') {
        await acknowledgeTemplateMessage(item.id);
      } else {
        await axios.post(
          `${API_URL}/announcements/${item.id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      dismissAnnouncement(item);
    } catch (error) {
      if (isDismissibleAckError(error)) {
        dismissAnnouncement(item);
        return;
      }
      console.error('Failed to acknowledge announcement:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleNext = () => {
    if (announcements.length > 0) {
      setCurrentIndex((currentIndex + 1) % announcements.length);
    }
  };

  const handlePrevious = () => {
    if (announcements.length > 0) {
      setCurrentIndex((currentIndex - 1 + announcements.length) % announcements.length);
    }
  };

  const handleOpenLinkedItem = async (item) => {
    if (!item?.id || openingLinkId) return;
    const kind = String(item.kind || '').toUpperCase();
    setOpeningLinkId(item.id);
    try {
      if (kind === 'TASK') {
        const task = await dispatch(fetchTaskById(item.id)).unwrap();
        if (task) setViewingTask(task);
        return;
      }
      if (kind === 'WORKFLOW' && item.messageId) {
        const response = await axios.get(`${API_URL}/messages/${item.messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const workflow = messageToWorkflowItem(response.data);
        if (workflow) setViewingWorkflow(workflow);
      }
    } catch (error) {
      console.error('Failed to open linked item:', error);
    } finally {
      setOpeningLinkId('');
    }
  };

  const getTypeAccent = (type, isEvent) => {
    if (isEvent) {
      return {
        wash: 'from-fuchsia-500 via-sky-400 to-amber-400',
        washAlt: 'from-rose-400/80 via-transparent to-cyan-400/70',
        card: 'from-violet-600 via-fuchsia-500 to-amber-400',
        blobA: 'bg-cyan-300',
        blobB: 'bg-rose-400',
        blobC: 'bg-amber-300',
        badge: 'bg-white/25 text-white',
        button: 'bg-white text-violet-700 hover:bg-violet-50',
      };
    }
    switch (type) {
      case 'WARNING':
        return {
          wash: 'from-amber-400 via-orange-500 to-rose-500',
          washAlt: 'from-yellow-300/80 via-transparent to-red-400/70',
          card: 'from-amber-500 via-orange-500 to-rose-500',
          blobA: 'bg-yellow-300',
          blobB: 'bg-rose-400',
          blobC: 'bg-orange-300',
          badge: 'bg-white/25 text-white',
          button: 'bg-white text-orange-700 hover:bg-orange-50',
        };
      case 'SUCCESS':
        return {
          wash: 'from-emerald-400 via-teal-500 to-cyan-400',
          washAlt: 'from-lime-300/80 via-transparent to-sky-400/70',
          card: 'from-emerald-500 via-teal-500 to-sky-500',
          blobA: 'bg-lime-300',
          blobB: 'bg-cyan-400',
          blobC: 'bg-emerald-300',
          badge: 'bg-white/25 text-white',
          button: 'bg-white text-emerald-700 hover:bg-emerald-50',
        };
      case 'ERROR':
        return {
          wash: 'from-rose-500 via-pink-500 to-orange-400',
          washAlt: 'from-fuchsia-400/80 via-transparent to-amber-400/70',
          card: 'from-rose-600 via-pink-500 to-orange-400',
          blobA: 'bg-pink-300',
          blobB: 'bg-amber-300',
          blobC: 'bg-rose-400',
          badge: 'bg-white/25 text-white',
          button: 'bg-white text-rose-700 hover:bg-rose-50',
        };
      case 'INFO':
      default:
        return {
          wash: 'from-sky-400 via-indigo-500 to-fuchsia-500',
          washAlt: 'from-cyan-300/80 via-transparent to-pink-400/70',
          card: 'from-sky-500 via-indigo-500 to-fuchsia-500',
          blobA: 'bg-cyan-300',
          blobB: 'bg-fuchsia-400',
          blobC: 'bg-indigo-300',
          badge: 'bg-white/25 text-white',
          button: 'bg-white text-indigo-700 hover:bg-indigo-50',
        };
    }
  };

  if (hideOnAnnouncementPanel || !token || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  if (!currentAnnouncement) return null;

  const isEventAnnouncement = Boolean(currentAnnouncement.eventId);
  const accent = getTypeAccent(currentAnnouncement.type, isEventAnnouncement);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 sm:p-6 announcement-popup-root">
      <div
        className={`absolute inset-0 bg-gradient-to-br ${accent.wash} announcement-popup-backdrop`}
        aria-hidden="true"
      />
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-tr ${accent.washAlt} announcement-popup-wash`}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <span className={`absolute -top-24 -left-16 w-80 h-80 rounded-full blur-3xl opacity-70 ${accent.blobA}`} />
        <span className={`absolute top-1/3 -right-20 w-96 h-96 rounded-full blur-3xl opacity-60 ${accent.blobB}`} />
        <span className={`absolute -bottom-24 left-1/4 w-72 h-72 rounded-full blur-3xl opacity-70 ${accent.blobC}`} />
      </div>

      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {blastPieces.map((piece) => (
          <span
            key={`${blastKey}-${piece.id}`}
            className={`announcement-blast-piece announcement-blast-shape-${piece.shape}`}
            style={{
              '--tx': `${piece.tx}px`,
              '--ty': `${piece.ty}px`,
              '--rot': `${piece.rot}deg`,
              '--size': `${piece.size}px`,
              '--delay': `${piece.delay}s`,
              '--duration': `${piece.duration}s`,
              backgroundColor: piece.color,
              left: '50%',
              top: '42%',
            }}
          />
        ))}
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={`spark-${blastKey}-${i}`}
            className="announcement-spark"
            style={{
              '--sx': `${(Math.random() - 0.5) * 480}px`,
              '--sy': `${-80 - Math.random() * 260}px`,
              '--delay': `${0.05 + Math.random() * 0.35}s`,
              backgroundColor: BLAST_COLORS[i % BLAST_COLORS.length],
              left: '50%',
              top: '45%',
            }}
          />
        ))}
      </div>

      <div
        className="relative w-full max-w-xl announcement-popup-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-popup-title"
      >
        <div className={`relative overflow-hidden rounded-3xl shadow-[0_24px_80px_rgba(0,0,0,0.35)] bg-gradient-to-br ${accent.card}`}>
          <div className={`pointer-events-none absolute -top-16 -right-10 w-52 h-52 rounded-full blur-3xl opacity-80 ${accent.blobA}`} />
          <div className={`pointer-events-none absolute -bottom-20 -left-12 w-60 h-60 rounded-full blur-3xl opacity-70 ${accent.blobC}`} />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/15" />

          <div className="relative p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className="announcement-popup-badge flex-shrink-0 w-14 h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-lg ring-2 ring-white/40">
                <svg className="w-7 h-7 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${accent.badge}`}>
                    {isEventAnnouncement ? 'Event' : 'Announcement'}
                  </span>
                  {currentAnnouncement.source !== 'template' && currentAnnouncement.category && (
                    <span className="px-2.5 py-1 text-xs font-semibold bg-white/20 text-white rounded-full">
                      {currentAnnouncement.category}
                    </span>
                  )}
                </div>
                <h3
                  id="announcement-popup-title"
                  className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight drop-shadow-sm"
                >
                  {currentAnnouncement.title}
                </h3>
              </div>
            </div>

            <div className="mb-7 max-h-[28rem] sm:max-h-[32rem] overflow-y-auto rounded-2xl bg-white/95 px-5 py-4 shadow-inner">
              <p className="text-gray-800 whitespace-pre-wrap leading-7 text-base sm:text-lg font-medium">
                {currentAnnouncement.content}
              </p>
              {currentAnnouncement.eventId ? (
                <AnnouncementPollPreview
                  eventId={currentAnnouncement.eventId}
                  currentUserId={user?.id}
                />
              ) : null}
              {(currentAnnouncement.linkedItems || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Related
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {currentAnnouncement.linkedItems.map((item) => {
                      const kind = String(item.kind || '').toUpperCase();
                      if (kind !== 'TASK' && kind !== 'WORKFLOW') return null;
                      return (
                        <button
                          key={`${kind}:${item.id}`}
                          type="button"
                          onClick={() => handleOpenLinkedItem(item)}
                          disabled={openingLinkId === item.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-60"
                        >
                          <span className="text-[10px] uppercase tracking-wide text-gray-400">
                            {kind === 'WORKFLOW' ? 'Workflow' : 'Task'}
                          </span>
                          {item.title || (kind === 'WORKFLOW' ? 'Workflow' : 'Task')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              {announcements.length > 1 ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="p-2.5 text-white/80 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
                    title="Previous"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-white tabular-nums px-1 font-medium">
                    {currentIndex + 1} / {announcements.length}
                  </span>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="p-2.5 text-white/80 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
                    title="Next"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div />
              )}

              <button
                type="button"
                onClick={() => handleAcknowledge(currentAnnouncement)}
                disabled={acknowledging}
                className={`announcement-ack-btn inline-flex items-center gap-2 px-6 py-3 text-base font-bold rounded-xl shadow-lg transition-transform disabled:opacity-60 disabled:cursor-not-allowed ${accent.button}`}
              >
                {acknowledging ? (
                  'Acknowledging…'
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {isEventAnnouncement ? 'Open event' : 'Acknowledge'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {viewingTask && (
        <TaskViewDialog
          task={viewingTask}
          onClose={() => setViewingTask(null)}
          usePortal
          zIndexClass="z-[2200]"
        />
      )}
      {viewingWorkflow && (
        <WorkflowViewDialog
          item={viewingWorkflow}
          onClose={() => setViewingWorkflow(null)}
          zIndexClass="z-[2200]"
        />
      )}
    </div>,
    document.body
  );
};

export default AnnouncementPopup;
